import { Popup } from '@progress/kendo-react-popup'
import DateInput from 'components/DateInput'
import SavingStates, { SavingStatus } from 'components/Spinners/Saving'
import TimeInput from 'components/TimeInput'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useEffect, useRef } from 'react'
import { useState } from 'react'
import { ErrorMessage } from 'types/error-message'
import { LocationItem, LocationSpace } from 'types/location'
import { classNames } from 'utils/dom-helpers'
import { postApiRequest, putApiRequest } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { toTitleCase } from 'utils/string-helper'
import { addDurationToStr, isSameDay, strToDate, toHHMM, toYmd } from 'utils/string-localization-helper'
import { GroupClassApi, GroupClassAvailabilityApi, ModalDataAttributes, RecurrenceSchedule } from '../types'
import { DeleteIcon } from 'utils/custom-icons'

const days_of_week = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
]

export const repeat_options = [
    RecurrenceSchedule.WEEKLY,
    RecurrenceSchedule.MONTHLY_ON_DATE,
    RecurrenceSchedule.MONTHLY_ON_DAY,
    RecurrenceSchedule.DAILY,
]

function isPast(d: unknown): boolean {
    if (d == undefined) return false
    return d ? (
        (d as Date).toISOString().substr(0, 10) <
        new Date().toISOString().substr(0, 10)
    ) : false
}

type MissingColumnProps = {
    content_key: string
    popup_content_key: string
}

function MissingColumn(props: MissingColumnProps) {
    const ref = useRef<HTMLInputElement>(null)
    const [show_popup, togglePopup] = useState<boolean>(false)
    return (
        <div className="w-full"
            ref={ref}
            onMouseEnter={() => {
                togglePopup(true)
            }}
            onMouseLeave={() => {
                togglePopup(false)
            }}
        >
            <Translation
                render_as="div"
                className="`w-full focus:ring-primary-light focus:border-primary-light shadow-sm border-gray-300 rounded-md text-sm"
                content_key={props.content_key}
            /><Popup
                anchor={ref.current}
                show={show_popup}
                animate={false}
                popupAlign={{
                    horizontal: 'left',
                    vertical: 'top',
                }}
                popupClass={
                    'bg-black text-white text-xs shadow-lg rounded-xl w-auto px-2 py-1 mt-2'
                }
                className="z-[102]"
            >
                <Translation
                    render_as="p"
                    content_key={props.popup_content_key}
                />
            </Popup>
        </div>
    )
}

function SeriesAvailabilitiesCells({
    cell_index,
    series_index,
    group_class,
    availability,
    select_date_text,
    handleAvailabilityChange,
    handleRemoveAvailability
}: {
    cell_index: number
    series_index: number
    group_class: ModalDataAttributes
    availability: ModalDataAttributes
    select_date_text: string
    handleAvailabilityChange(key: string, value: any, series_index: number, cell_index: number): void
    handleRemoveAvailability(series_index: number, cell_index: number): void
}) {
    const [timer, setTimer] = useState<any>()
    const { translations } = useAppContext()
    let date = availability.date ? new Date((availability.date as Date)?.toDateString()) : undefined
    let start_time = availability.start_time

    return (
        <div className="w-full flex flex-row items-center gap-4 duration-300 justify-start">
            <div className="flex flex-row" key={series_index + "_" + cell_index} >
                <Translation
                    render_as="label"
                    content_key="date"
                    className="inline-block flex mr-2 my-auto"
                />
                <DateInput
                    id={`series_date_${series_index}_${cell_index}`}
                    name={`series_date_${series_index}_${cell_index}`}
                    minDate={new Date()}
                    className='border-0 rounded-l-md text-sm focus:ring-0 inline-block flex'
                    onChange={(d: Date) => {
                        if (d != date) {
                            date = d
                            handleAvailabilityChange("date", date, series_index, cell_index)
                        }
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={select_date_text}
                    selected={date ? new Date(date) : undefined}
                    popperClassName="z-10"
                />
                <div
                    className="flex items-center rounded-r-md focus:ring-0 bg-white"
                >
                    <label >
                        <i className="feather-calendar px-2 "></i>
                    </label>
                </div>
            </div>
            <div className="flex flex-row" >
                <Translation
                    render_as="label"
                    content_key="time"
                    className="inline-block flex mr-2 my-auto"
                />
                <TimeInput
                    id={`series_start_time${series_index}_${cell_index}`}
                    name={`series_start_time${series_index}_${cell_index}`}
                    min={
                        date && isSameDay(date, new Date())
                            ? date && date > new Date()
                                ? group_class.date as Date
                                : new Date()
                            : undefined
                    }
                    onChange={(e) => {
                        if (e.target.value && availability.date != e.target.value) {
                            start_time = toHHMM(e.target.value)
                            if (timer) {
                                clearTimeout(timer)
                                setTimer(undefined)
                            }
                            setTimer(setTimeout(async () => {
                                handleAvailabilityChange("start_time", start_time, series_index, cell_index)
                            }, 1000))
                        }
                    }}
                    value={
                        start_time ? strToDate(date ? date as Date : new Date(), start_time as string) : new Date()
                    }
                />
            </div>
            <span 
            onClick={() => {
                handleRemoveAvailability(series_index, cell_index)
            }}
            title={getTranslation(
                'Delete',
                translations
            )} 
                >
                <DeleteIcon/>
            </span>
        </div>
    )
}

export default function GroupClassRowForm({
    group_class,
    idx,
    is_series,
    staff_items,
    open_series,
    onAttributesChanged,
    duplicateRow,
    removeHandler,
    setError,
    toggleHasLocal
}: {
    group_class: ModalDataAttributes
    idx: number
    is_series: boolean
    staff_items: Record<string, string>[]
    open_series: boolean
    onAttributesChanged(u: ModalDataAttributes, idx: number): void
    duplicateRow(idx: number, is_add?: boolean): void
    removeHandler(is_local: boolean): void
    setError(error: ErrorMessage): void
    toggleHasLocal(has_local: boolean): void
}) {
    const { locations, getGroupClasses } = useTenantContext()
    const { translations } = useAppContext()
    const { ServiceModal } = useAuth()
    const { attributes, setAttributes } = ServiceModal
    const disable = !!group_class?.id && !!group_class?.is_recurring && !is_series
    const style = disable ? 'bg-gray-300 cursor-not-allowed' : ''
    const is_local = !group_class?.id
    const [show_series, toggleShowSeries] = useState<boolean>(group_class?.show_series != undefined ? group_class.show_series as boolean : open_series)
    const [has_end_date, toggleEndDate] = useState<boolean>(!!group_class.ends_at)
    const location: LocationItem | undefined = locations.find((l: LocationItem) => l.id == group_class?.location)
    const spaces: LocationSpace[] = location?.location_spaces || []
    const [timer, setTimer] = useState<any>()
    const [status, setStatus] = useState<SavingStatus>(SavingStatus.NONE)
    const [save_timer, setSaveTimer] = useState<any>()
    const [is_allow_apply, toggleIsAllowApply] = useState<boolean>(false)
    const [select_date_text, setSelectDateText] = useState<string>("")

    useEffect(() => {
        setSelectDateText(getTranslation("common.select_date", translations))
    }, [translations])

    useEffect(() => {
        toggleIsAllowApply(!!group_class.starts_at && !!group_class.ends_at && !!group_class.repeats && !!group_class.start_time)
    }, [group_class.starts_at, group_class.ends_at, group_class.repeats, !!group_class.start_time])

    useEffect(() => {
        group_class.show_series = show_series
    }, [show_series])

    async function handleSaveSeries() {
        try {
            setStatus(SavingStatus.SAVING)
            const payload: GroupClassApi = {
                id: group_class?.id as string,
                name: group_class?.name as string,
                serviceId: attributes?.id as string,
                groupClassAvailabilities: ((group_class?.availabilities as ModalDataAttributes[]))
                    ?.map((availability: any) => {
                        return ({
                            id: availability?.id,
                            date: availability.date,
                            startTime: availability.start_time,
                            endTime: addDurationToStr(availability.date, availability.start_time, group_class.duration as number)
                        }) as GroupClassAvailabilityApi
                    }),
                groupClassSetting: {
                    id: group_class?.setting_id as string,
                    locationId: group_class?.location as string,
                    locationSpaceId: group_class?.location_space as string,
                    staffId: group_class?.staff as string
                }
            }
            let res
            if (payload?.id) {
                res = await putApiRequest(
                    '/v1/group_classes/series',
                    payload
                )
            } else {
                res = await postApiRequest(
                    '/v1/group_classes/series',
                    payload
                )
            }
            if (res?.ok) {
                if (is_local) {
                    toggleHasLocal(false)
                }
                setStatus(SavingStatus.SAVED_SUCCESS)
                if (save_timer) {
                    clearTimeout(save_timer)
                    setSaveTimer(undefined)
                }
                setSaveTimer(setTimeout(async () => {
                    setStatus(SavingStatus.NONE)
                }, 7000))
                getGroupClasses()
            } else {
                setStatus(SavingStatus.SAVED_FAILURE)
                setError({ tag: res.tag, message: res.message })
            }
        } catch (e) {
            setStatus(SavingStatus.SAVED_FAILURE)
            console.log(e)
        }
    }

    function sortGroupClassSeries(gc: ModalDataAttributes): ModalDataAttributes {
        gc.availabilities = (gc.availabilities as ModalDataAttributes[])
            .sort((a, b) => {
                return a.date && b.date ? (a?.date as Date)?.getTime() - (b?.date as Date)?.getTime() : 0
            })
        gc.date = gc.availabilities[0].date
        gc.time = toHHMM(gc.availabilities[0].date as Date)
        return gc
    }

    function handleAvailabilityChange(key: string, value: any, series_index: number, cell_index: number) {
        (group_class.availabilities as ModalDataAttributes[])[cell_index][key] = value
        group_class = sortGroupClassSeries(group_class)
        onAttributesChanged(group_class, series_index)
    }

    function handleRemoveAvailability(series_index: number, cell_index: number) {
        if ((group_class.availabilities as ModalDataAttributes[]).length == 1) {
            group_class.availabilities = []
        } else {
            (group_class.availabilities as ModalDataAttributes[]).splice(cell_index, 1)
        }
        onAttributesChanged(group_class, series_index)
    }

    function handleApplyRecurring() {
        var rec: ModalDataAttributes[] = new Array<ModalDataAttributes>()
        var date = new Date(group_class.starts_at as Date)
        const time = group_class.start_time
        let increment
        switch (group_class.repeats as RecurrenceSchedule) {
            case RecurrenceSchedule.MONTHLY_ON_DAY:
                increment = () => {
                    const day_of_week = date.getDay()
                    const num_of_week = Math.ceil(date.getDate() / 7)
                    let new_date = new Date(date.getFullYear(), date.getMonth() + 1)
                    new_date.setDate(1 + (7 - new_date.getDay() + day_of_week) % 7 + (num_of_week - 1) * 7)
                    date = new_date
                }
                break
            case RecurrenceSchedule.MONTHLY_ON_DATE:
                increment = () => {
                    date.setMonth(date.getMonth() + 1)
                }
                break
            case RecurrenceSchedule.WEEKLY:
                increment = () => {
                    date.setDate(date.getDate() + 7)
                }
                break
            default: //Default is DAILY
                increment = () => {
                    date.setDate(date.getDate() + 1)
                }
                break
        }

        do {
            rec.push({
                date: new Date(date.toDateString()),
                start_time: time
            })
            increment()
        } while (date.getTime() <= group_class.ends_at)
        group_class.availabilities = rec
        onAttributesChanged(group_class, idx)
    }

    return (
        <div>
            <div
                className={classNames(
                    'flex gap-2 px-2 h-16 overflow-hidden items-center '
                )}
            >
                {is_series &&
                    <>
                        <button
                            onClick={() => {
                                toggleShowSeries(!show_series)
                            }}
                            className={classNames(show_series ? "-rotate-0" : "-rotate-180",
                                "feather-chevron-down transition-transform duration-300 text-xs text-grey-700 font-bold"
                            )}
                        />
                        <div className="flex flex-auto w-40">
                            <input
                                type={"text"}
                                disabled={disable}
                                id="series_class_name"
                                name="series_class_name"
                                className={`w-full focus:ring-primary-light focus:border-primary-light
                                     shadow-sm border-gray-300 rounded-md text-sm ${style}`}
                                onChange={(e) => {
                                    group_class.name = e.target.value
                                    if (timer) {
                                        clearTimeout(timer)
                                        setTimer(undefined)
                                    }
                                    setTimer(setTimeout(async () => {
                                        onAttributesChanged(group_class, idx)
                                    }, 1000))
                                }}
                                value={group_class.name as string}
                            />
                        </div>
                    </>

                }
                <div className="flex flex-auto w-40">
                    {locations && locations.length ? (
                        <select
                            disabled={disable}
                            id="location"
                            name="location"
                            className={`w-full focus:ring-primary-light focus:border-primary-light shadow-sm border-gray-300 rounded-md text-sm ${style}`}
                            onChange={(e) => {
                                group_class.location = e.target.value
                                group_class.location_space = locations?.find((l: LocationItem) => l.id == e.target.value)?.location_spaces?.[0]?.id || ""
                                onAttributesChanged(group_class, idx)
                            }}
                            value={(group_class.location && group_class.location) as string}
                        >
                            {locations.map(
                                ({
                                    name,
                                    id,
                                }: LocationItem) => (
                                    <option key={id} value={id}>
                                        {name}
                                    </option>
                                )
                            )}
                        </select>
                    ) : (
                        <div className="bg-white w-full block h-full bg-gray-150 rounded-lg" />
                    )}
                </div>
                <div className="flex flex-auto w-40"
                >
                    {spaces?.length ? (
                        <select
                            disabled={disable}
                            id="location-space"
                            name="location-space"
                            className={`w-full focus:ring-primary-light focus:border-primary-light shadow-sm border-gray-300 rounded-md text-sm ${style}`}
                            onChange={(e) => {
                                group_class.location_space = e.target.value
                                onAttributesChanged(group_class, idx)
                            }}
                            value={
                                (group_class.location_space &&
                                    group_class.location_space) as string
                            }
                        >
                            {spaces.map(
                                ({
                                    name,
                                    id,
                                }: LocationSpace) => (
                                    <option key={id} value={id}>
                                        {name}
                                    </option>
                                )
                            )}
                        </select>
                    ) : (
                        <MissingColumn
                            content_key="no_spaces_available"
                            popup_content_key="no_spaces_popup"
                        />
                    )}
                </div>

                <div className="flex flex-auto w-40 place-items-stretch ">
                    {staff_items && staff_items.length ? (
                        <select
                            disabled={disable}
                            id="staff"
                            name="staff"
                            className={` w-full focus:ring-primary-light focus:border-primary-light shadow-sm border-gray-300 rounded-md text-sm ${style}`}
                            onChange={(e) => {
                                group_class.staff = e.target.value
                                onAttributesChanged(group_class, idx)
                            }}
                            value={(group_class.staff && group_class.staff) as string}
                        >
                            {staff_items.map(
                                ({
                                    id,
                                    name,
                                }: Record<string, string>) => (
                                    <option key={id} value={id}>
                                        {name}
                                    </option>
                                )
                            )}
                        </select>
                    ) : (
                        <div className="bg-white w-full block h-full bg-gray-150 rounded-lg" />
                    )}
                </div>

                {!is_series &&
                    <>
                        <div className="flex flex-auto w-36 ">
                            <div
                                style={{ borderWidth: '1px' }}
                                className={classNames(
                                    'flex items-stretch focus:ring-primary-light focus:border-primary-light w-full shadow-sm border-gray-300 rounded-md text-sm',
                                    style,
                                    disable ? 'text-gray-500' : 'text-inherit'
                                )}
                            >
                                <DateInput
                                    id={`classDate1${idx}`}
                                    minDate={new Date()}
                                    disabled={disable}
                                    className={classNames(
                                        'border-0 rounded-l-md text-sm focus:ring-0',
                                        disable ? 'text-gray-500' : 'text-inherit',
                                        style
                                    )}
                                    onChange={(date: Date) => {
                                        group_class.date = date
                                        group_class.day_of_week = days_of_week[date.getDay()]
                                        onAttributesChanged(group_class, idx)
                                    }}
                                    dateFormat="dd/MM/yyyy"
                                    selected={group_class.date as Date}
                                />
                                <div
                                    className={classNames(
                                        style,
                                        'flex items-center rounded-r-md focus:ring-0',
                                        disable ? '' : 'bg-white'
                                    )}
                                >
                                    <label htmlFor={`classDate1${idx}`}>
                                        <i className="feather-calendar pr-2 "></i>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-auto w-36 place-items-stretch px-1 ">
                            <TimeInput
                                min={group_class.date > new Date() ? undefined : new Date()}
                                disabled={disable}
                                onChange={(e) => {
                                    if (e.target.value && e.value) {
                                        group_class.time = toHHMM(e.target.value)
                                        group_class.date = strToDate(group_class.date as Date, group_class.time)
                                        onAttributesChanged(group_class, idx)
                                    }
                                }}
                                defaultValue={
                                    group_class.date ? new Date(group_class.date as Date) : new Date()
                                }
                            />
                        </div>
                    </>
                }


                <div className="flex flex-auto items-center w-44 flex px-1 gap-2">
                    <input
                        id={`check_${idx}`}
                        className={classNames(
                            'h-6 w-6 border-gray-300 rounded focus:ring-primary-light',
                            isPast(group_class.date) || disable
                                ? 'text-gray-200'
                                : 'text-primary'
                        )}
                        type="checkbox"
                        disabled={(isPast(group_class.date) || disable) && !is_series}
                        defaultChecked={group_class.is_recurring as boolean}
                        onChange={(v) => {
                            group_class.is_recurring = v.target.checked
                            if (v.target.checked) {
                                group_class.repeats = RecurrenceSchedule.WEEKLY
                                const start_from_date: Date = group_class.date
                                    ? (group_class.date as Date)
                                    : new Date()
                                group_class.day_of_week =
                                    days_of_week[start_from_date.getDay()]
                                group_class.start_time = toHHMM(new Date())
                            } else {
                                delete group_class.day_of_week
                                delete group_class.ends_at
                                delete group_class.start_time
                                group_class.repeats = RecurrenceSchedule.NONE
                            }
                            onAttributesChanged(group_class, idx)
                        }}
                    />
                    {group_class.date < toYmd(new Date()) || ''}
                    <Translation
                        render_as="label"
                        htmlFor={`check_${idx}`}
                        content_key="recurring_label"
                    />
                    {!is_series &&
                        <>
                            <div className="flex-1" />
                            <button
                                type="button"
                                className={`text-primary bg-opacity-30 h-10 w-10 rounded-lg text-xl feather-copy`}
                                onClick={() => {
                                    duplicateRow(idx)
                                }}
                            />
                        </>
                    }

                    
                    <span
                    onClick={() => removeHandler(is_local)}
                    title={getTranslation(
                        'Delete',
                        translations
                    )}
                    >
                        <DeleteIcon/>
                    </span>
                </div>
            </div>
            {group_class.is_recurring &&
                <div
                    className={
                        'w-full flex flex-col px-7 bg-gray-50 py-4 border-t border-primary-lighter'
                    }
                >
                    <div className={classNames(
                        'w-full flex flex-row w-full gap-6',
                        is_series
                            ? "justify-center"
                            : "justify-between"
                    )}
                    >
                        <label
                            htmlFor="day_of_week"
                            className={classNames("flex gap-3 items-center",
                                is_series ? "order-5" : "order-1"
                            )}
                        >
                            <Translation content_key="day_label" />
                            <input
                                disabled
                                id="day_of_week"
                                name="day_of_week"
                                className="w-40 h-full py-2 px-3 focus:ring-primary-light focus:border-primary-light shadow-sm border-gray-300 rounded-md text-sm bg-gray-300"
                                onChange={(v) => {
                                    group_class.day_of_week = v.target.value

                                    onAttributesChanged(group_class, idx)
                                }}
                                value={
                                    toTitleCase(
                                        days_of_week[
                                        new Date(is_series && group_class?.starts_at ? group_class.starts_at as Date : group_class.date as Date).getDay()
                                        ]
                                    )
                                }
                            >
                            </input>
                        </label>

                        <label
                            htmlFor="repeats"
                            className="flex gap-3 items-center order-2"
                        >
                            <Translation content_key="repeats_label" />
                            <div className="w-40">
                                <select
                                    disabled={disable}
                                    id="repeats"
                                    name="repeats"
                                    className={`w-40 focus:ring-primary-light focus:border-primary-light shadow-sm border-gray-300 rounded-md text-sm ${style}`}
                                    value={(group_class.repeats as string) || ''}
                                    onChange={(v) => {
                                        const value = v.target.value
                                        group_class.repeats = value
                                        if (
                                            value !==
                                            RecurrenceSchedule.DAILY
                                        ) {
                                            group_class.day_of_week =
                                                days_of_week[
                                                new Date(
                                                    group_class.date as Date
                                                ).getDay()
                                                ]
                                        }

                                        onAttributesChanged(group_class, idx)
                                    }}
                                >
                                    {repeat_options.map((d: string) => (
                                        <option key={d} value={d}>
                                            {translations[
                                                d.toLowerCase()
                                            ] || d.toLowerCase()}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </label>
                        {is_series ? (
                            <>

                                <label
                                    htmlFor="starts_at"
                                    className="flex gap-3 items-center order-3"
                                >
                                    <Translation content_key="start_date" />
                                    <div className="flex items-center gap-1">
                                        <div
                                            style={{ borderWidth: '1px' }}
                                            className={classNames(
                                                'flex items-stretch focus:ring-primary-light focus:border-primary-light w-full shadow-sm border-gray-300 rounded-md text-sm w-36',
                                            )}
                                        >
                                            <DateInput
                                                id={`starts_at_${idx}`}
                                                minDate={new Date(group_class.date as string)}
                                                className={classNames(
                                                    'border-0 rounded-l-md text-sm focus:ring-0 inline-block flex',
                                                )}
                                                onChange={(date: Date) => {
                                                    group_class.starts_at = date
                                                    group_class.day_of_week = days_of_week[date.getDay()]
                                                    onAttributesChanged(group_class, idx)
                                                }}
                                                dateFormat="dd/MM/yyyy"
                                                selected={group_class.starts_at as Date}
                                                popperClassName="z-20"
                                            />
                                            <div
                                                className={classNames(
                                                    'flex items-center rounded-r-md focus:ring-0 bg-white',
                                                )}
                                            >
                                                <label htmlFor={`starts_at_${idx}`}>
                                                    <i className="feather-calendar pr-2 "></i>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <label
                                    htmlFor="starts_at"
                                    className="flex gap-3 items-center order-4"
                                >
                                    <Translation
                                        content_key="time"
                                    />
                                    <TimeInput
                                        id={`start_time_${idx}`}
                                        min={group_class.starts_at && group_class.starts_at > new Date() ? new Date((group_class.starts_at as Date).toDateString()) : new Date()}
                                        onChange={(e) => {
                                            if (e.target.value && group_class.start_time != e.target.value) {
                                                group_class.start_time = toHHMM(e.target.value)
                                                onAttributesChanged(group_class, idx)
                                            }
                                        }}
                                        value={
                                            group_class.start_time ? strToDate(group_class.starts_at ? group_class.starts_at as Date : new Date(), group_class.start_time as string) : new Date()
                                        }
                                    />
                                </label>
                            </>

                        ) : (
                            <div className="flex-1 order-4" />
                        )}
                        <label
                            htmlFor="end_date"
                            className="flex gap-3 items-center h-10 order-6"

                        >
                            <Translation content_key="end_date_label" />
                            {has_end_date || is_series ? (
                                <div className="flex items-center gap-1">
                                    <div
                                        style={{ borderWidth: '1px' }}
                                        className={classNames(
                                            'flex items-stretch focus:ring-primary-light focus:border-primary-light w-full shadow-sm border-gray-300 rounded-md text-sm w-36',
                                        )}
                                    >
                                        <DateInput
                                            id={`ends_at_${idx}`}
                                            minDate={new Date(group_class.date as string)}
                                            className={classNames(
                                                'border-0 rounded-l-md text-sm focus:ring-0 inline-block flex',
                                            )}
                                            onChange={(date: Date) => {
                                                date.setHours(23)
                                                date.setMinutes(59)
                                                group_class.ends_at = date
                                                onAttributesChanged(group_class, idx)
                                            }}
                                            dateFormat="dd/MM/yyyy"
                                            selected={group_class.ends_at as Date}
                                            popperClassName="z-20"
                                        />
                                        <div
                                            className={classNames(
                                                'flex items-center rounded-r-md focus:ring-0 bg-white',
                                            )}
                                        >
                                            <label htmlFor={`ends_at_${idx}`}>
                                                <i className="feather-calendar pr-2 "></i>
                                            </label>
                                        </div>
                                    </div>
                                    {is_series ? (
                                        <button
                                            type="submit"
                                            disabled={!is_allow_apply}
                                            className={classNames(
                                                `relative ml-5 w-20 h-9 flex justify-center
                                            py-2 px-6 border border-transparent
                                            rounded text-white
                                            focus:outline-none bg-primary-light`,
                                                !is_allow_apply ? "opacity-40 cursor-not-allowed" : ""
                                            )}
                                            onClick={handleApplyRecurring}
                                        >
                                            <Translation
                                                content_key="services.apply"
                                            />
                                        </button>
                                    ) : (
                                        <i
                                            className="feather-x-circle text-primary-light text-lg cursor-pointer"
                                            onClick={() => {
                                                delete group_class.ends_at
                                                onAttributesChanged(group_class, idx)
                                                toggleEndDate(false)
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 cursor-pointer"
                                    onClick={() => {
                                        toggleEndDate(true)
                                        const today = new Date()
                                        group_class.ends_at = today.setDate(today.getDate() + 1)
                                        onAttributesChanged(group_class, idx)
                                    }}
                                >
                                    <i className="feather-plus text-primary-light text-lg" />
                                    <div className='text-primary-light font-sans font-light text-sm flex justify-between w-32'>
                                        <Translation
                                            render_as="span"
                                            content_key="set_end_date_label"
                                        />
                                        <label htmlFor={`ends_at_${idx}`}>
                                            <i className="feather-calendar pr-2 "></i>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </label>
                    </div>

                    {is_allow_apply &&
                        <div
                            className="w-full font-[450] text-sm mt-3"
                        >
                            <i className="feather-alert-triangle text-xl text-yellow-500 mr-3" />
                            <Translation
                                content_key="services.series_note"
                            />
                        </div>
                    }

                </div>
            }


            {is_series &&
                <div className={classNames("w-full pr-4 pl-9 bg-gray-50 transition-all duration-300 ease-out border-t border-[#DDE2E5]",
                    show_series ? "py-6" : "h-0"
                )}>
                    <div className="w-full flex flex-row grid grid-cols-1 origin-top-left flex-wrap block gap-x-8 gap-y-3">
                        {group_class?.availabilities && (group_class.availabilities as ModalDataAttributes[]).map((availability: ModalDataAttributes, index: number) => {
                            return (
                                <SeriesAvailabilitiesCells
                                    key={availability?.id as string}
                                    cell_index={index}
                                    series_index={idx}
                                    group_class={group_class}
                                    availability={availability}
                                    select_date_text={select_date_text}
                                    handleAvailabilityChange={handleAvailabilityChange}
                                    handleRemoveAvailability={handleRemoveAvailability}
                                />
                            )
                        })
                        }
                    </div>
                    <div className="w-full flex flex-row relative mt-4">
                        <button className="text-primary-light py-1.5 px-3 flex border border-primary-light rounded bg-white text-center"
                            onClick={() => {
                                (group_class.availabilities as ModalDataAttributes[]).push({
                                    date: "",
                                    start_time: toHHMM(new Date())
                                })
                                onAttributesChanged(group_class, idx)
                            }}
                        >
                            <i className="feather-plus text-sm mr-2 inline" />
                            <Translation
                                render_as="label"
                                className="my-auto"
                                content_key="common.add_another_class_in_series"
                            />
                        </button>
                        <button
                            type="submit"
                            className={classNames(
                                `relative mx-5 w-fit h-9 flex justify-center
                                    py-2 px-6 border border-transparent
                                    rounded text-white
                                    focus:outline-none`,
                                status == SavingStatus.SAVING
                                    ? 'bg-primary-light'
                                    : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                            )}
                            onClick={handleSaveSeries}
                        >
                            <Translation
                                content_key="services.class_series_save"
                            />
                        </button>
                        <SavingStates
                            status={status}
                        />
                        <Translation
                            render_as="label"
                            content_key={(group_class.availabilities as ModalDataAttributes[])?.length == 1 ? "common.one_class_added" : "common.x_classes_added"}
                            content_values={{
                                class_count: (group_class.availabilities as ModalDataAttributes[])?.length || 0
                            }}
                            className="flex right-0 absolute uppercase rounded-3xl py-2 px-4 bg-orange-500/[0.1] text-orange-600 font-bold"
                        />
                    </div>
                </div>
            }
        </div>
    )
}
